<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AnalyticsPanel from './AnalyticsPanel.vue';
import MetricValue from './MetricValue.vue';
import ProportionBar from './ProportionBar.vue';
import { useAnalyticsFormat } from './useAnalyticsFormat';
import { QUEM_CORES } from '../atendimento/quemCores';

// "A equipe depois da IA" (REPORTS-02): o que o livro de encaminhamentos conta das
// passagens do período — quem respondeu, em quanto tempo, e como terminou. Sem
// passagem, nada aqui tem denominador: o servidor manda null e o motivo, e a tela
// diz "sem dado (motivo)", nunca 0 nem "0 s".
//
// REPORTS-03 (R3-52): a comparação por atendente traz, de cada um, a 1ª resposta
// (mediana e p90), as resolvidas e as devolvidas, e a tela diz a chave de cada
// número. "Com uma pessoa agora" e os online vêm da Agora, lida uma vez, sem se
// refazer, com a hora da leitura (G11b).
const props = defineProps({
  // A resposta de GET power_agent_analytics/equipe; null enquanto não chegou.
  report: { type: Object, default: null },
  failed: { type: Boolean, default: false },
  // A resposta de GET atendimento/agora, a hora em que ela chegou, e a falha dela.
  now: { type: Object, default: null },
  nowAt: { type: Date, default: null },
  nowFailed: { type: Boolean, default: false },
});

const { t, locale } = useI18n();
const { number, percent, duration, clock } = useAnalyticsFormat();

const handedOff = computed(() => props.report?.handed_off ?? null);
const noHandoffsReason = computed(() =>
  props.report?.no_data_reason === 'no_handoffs'
    ? t('ANALYTICS_REPORTS.TEAM.NO_DATA_REASON.NO_HANDOFFS')
    : ''
);

// Percentual inteiro de um número sobre as passagens; sem passagem, nada.
const shareOf = count =>
  typeof count === 'number' && handedOff.value > 0
    ? Math.round((count / handedOff.value) * 100)
    : null;

const shareHint = count => {
  const value = shareOf(count);
  return value === null
    ? ''
    : t('ANALYTICS_REPORTS.TEAM.SHARE', { percent: percent(value) });
};

const replied = computed(() => props.report?.replied || null);
const firstReply = computed(() => props.report?.first_reply_seconds || null);
const unanswered = computed(() => props.report?.unanswered || null);

// A primeira resposta tem o próprio denominador: as passagens respondidas.
const firstReplyReason = computed(() => {
  if (noHandoffsReason.value) return noHandoffsReason.value;
  return firstReply.value?.samples === 0
    ? t('ANALYTICS_REPORTS.TEAM.NO_DATA_REASON.NO_REPLIES')
    : '';
});

const cards = computed(() => [
  {
    key: 'handed_off',
    label: t('ANALYTICS_REPORTS.TEAM.HANDED_OFF'),
    value: handedOff.value,
    format: number,
    hints: [t('ANALYTICS_REPORTS.TEAM.HANDED_OFF_HINT')],
  },
  {
    key: 'replied',
    label: t('ANALYTICS_REPORTS.TEAM.REPLIED'),
    value: replied.value?.total ?? null,
    format: number,
    reason: noHandoffsReason.value,
    hints: [
      shareHint(replied.value?.total),
      replied.value?.super_admin
        ? t('ANALYTICS_REPORTS.TEAM.REPLIED_SUPER_ADMIN', {
            count: number(replied.value.super_admin),
          })
        : '',
    ],
  },
  {
    key: 'first_reply',
    label: t('ANALYTICS_REPORTS.TEAM.FIRST_REPLY'),
    value: firstReply.value?.p50 ?? null,
    format: duration,
    reason: firstReplyReason.value,
    hints: [
      typeof firstReply.value?.p50 === 'number'
        ? t('ANALYTICS_REPORTS.TEAM.FIRST_REPLY_HINT')
        : '',
      typeof firstReply.value?.p90 === 'number'
        ? t('ANALYTICS_REPORTS.TEAM.FIRST_REPLY_P90', {
            value: duration(firstReply.value.p90),
          })
        : '',
    ],
  },
  {
    key: 'unanswered',
    label: t('ANALYTICS_REPORTS.TEAM.UNANSWERED'),
    value: unanswered.value?.total ?? null,
    format: number,
    reason: noHandoffsReason.value,
    hints: [
      shareHint(unanswered.value?.total),
      unanswered.value?.open
        ? t('ANALYTICS_REPORTS.TEAM.UNANSWERED_OPEN', {
            count: number(unanswered.value.open),
          })
        : '',
    ],
  },
]);

// Os caminhos de resolução da R0-14. Caminho novo que a tela não conhece aparece
// cru, e o que ninguém carimbou diz isso.
const pathLabels = computed(() => ({
  botao: t('ANALYTICS_REPORTS.TEAM.PATHS.BOTAO'),
  acao_em_massa: t('ANALYTICS_REPORTS.TEAM.PATHS.ACAO_EM_MASSA'),
  auto_resolve: t('ANALYTICS_REPORTS.TEAM.PATHS.AUTO_RESOLVE'),
  automacao: t('ANALYTICS_REPORTS.TEAM.PATHS.AUTOMACAO'),
  macro: t('ANALYTICS_REPORTS.TEAM.PATHS.MACRO'),
  kanban: t('ANALYTICS_REPORTS.TEAM.PATHS.KANBAN'),
  widget: t('ANALYTICS_REPORTS.TEAM.PATHS.WIDGET'),
  mute: t('ANALYTICS_REPORTS.TEAM.PATHS.MUTE'),
  api_publica: t('ANALYTICS_REPORTS.TEAM.PATHS.API_PUBLICA'),
  bot: t('ANALYTICS_REPORTS.TEAM.PATHS.BOT'),
  playground_reset: t('ANALYTICS_REPORTS.TEAM.PATHS.PLAYGROUND_RESET'),
}));

const pathLabel = path =>
  path
    ? pathLabels.value[path] || path
    : t('ANALYTICS_REPORTS.TEAM.PATHS.UNKNOWN');

// A passagem resolvida sem resposta de pessoa entra em "Resolvidas", e não em
// "Resolveram" do caminho, que fica debaixo de "Alguém respondeu". A linha diz
// quantas são e por onde foram resolvidas; sem nenhuma, some.
const withoutReplyHint = computed(() => {
  const withoutReply = props.report?.resolved?.without_reply;
  if (!withoutReply?.total) return '';
  const byPath = withoutReply.by_path || [];
  const paths =
    byPath.length === 1
      ? pathLabel(byPath[0].path)
      : byPath
          .map(item => `${pathLabel(item.path)} ${number(item.count)}`)
          .join(', ');
  return t('ANALYTICS_REPORTS.TEAM.RESOLVED_WITHOUT_REPLY', {
    count: number(withoutReply.total),
    paths,
  });
});

// Como terminaram: resolvidas (com o caminho) e devolvidas à IA, na cor da IA do
// mapa único (quemCores.js). Classes escritas por inteiro: o Tailwind só gera o que
// lê no fonte.
const endings = computed(() => [
  {
    key: 'resolved',
    label: t('ANALYTICS_REPORTS.TEAM.RESOLVED'),
    hint: withoutReplyHint.value,
    value: props.report?.resolved?.total ?? null,
    fill: 'fill-n-teal-9',
  },
  {
    key: 'returned',
    label: t('ANALYTICS_REPORTS.TEAM.RETURNED'),
    hint: t('ANALYTICS_REPORTS.TEAM.RETURNED_HINT'),
    value: props.report?.returned_to_ai ?? null,
    fill: QUEM_CORES.ia.fill,
  },
]);

const paths = computed(() => props.report?.resolved?.by_path || []);
const people = computed(() => props.report?.per_person || []);

// A chave de cada número por atendente, como o servidor a manda
// (`per_person_keys`): os números da mesma chave numa linha só, "Passagens,
// 1ª resposta e motivos: por quem respondeu primeiro".
const NUMBER_LABELS = computed(() => ({
  handoffs: t('ANALYTICS_REPORTS.TEAM.KEYS.NUMBERS.HANDOFFS'),
  first_reply_seconds: t('ANALYTICS_REPORTS.TEAM.KEYS.NUMBERS.FIRST_REPLY'),
  top_reasons: t('ANALYTICS_REPORTS.TEAM.KEYS.NUMBERS.TOP_REASONS'),
  resolved: t('ANALYTICS_REPORTS.TEAM.KEYS.NUMBERS.RESOLVED'),
  returned_to_ai: t('ANALYTICS_REPORTS.TEAM.KEYS.NUMBERS.RETURNED'),
}));
const KEY_LABELS = computed(() => ({
  first_responder: t('ANALYTICS_REPORTS.TEAM.KEYS.BY.FIRST_RESPONDER'),
  resolved_by: t('ANALYTICS_REPORTS.TEAM.KEYS.BY.RESOLVED_BY'),
  returned_by: t('ANALYTICS_REPORTS.TEAM.KEYS.BY.RETURNED_BY'),
  assignee_or_first_responder: t(
    'ANALYTICS_REPORTS.TEAM.KEYS.BY.ASSIGNEE_OR_FIRST_RESPONDER'
  ),
}));
const listOf = items =>
  new Intl.ListFormat(locale.value.replace('_', '-'), {
    type: 'conjunction',
  }).format(items);
const capitalized = text => text.charAt(0).toUpperCase() + text.slice(1);
const personKeys = computed(() => {
  const byKey = new Map();
  Object.entries(props.report?.per_person_keys || {}).forEach(
    ([field, key]) => {
      if (!NUMBER_LABELS.value[field] || !KEY_LABELS.value[key]) return;
      byKey.set(key, [...(byKey.get(key) || []), NUMBER_LABELS.value[field]]);
    }
  );
  return [...byKey.entries()].map(([key, fields]) =>
    t('ANALYTICS_REPORTS.TEAM.KEYS.LINE', {
      numbers: capitalized(listOf(fields)),
      key: KEY_LABELS.value[key],
    })
  );
});

// A 1ª resposta de um atendente: a mediana e o p90, ou "sem dado" sem resposta.
const personFirstReply = person => {
  const reply = person.first_reply_seconds;
  if (typeof reply?.p50 !== 'number')
    return t('ANALYTICS_REPORTS.TEAM.PERSON.NO_FIRST_REPLY');
  const median = t('ANALYTICS_REPORTS.TEAM.PERSON.FIRST_REPLY', {
    time: duration(reply.p50),
  });
  return typeof reply.p90 === 'number'
    ? t('ANALYTICS_REPORTS.TEAM.PERSON.FIRST_REPLY_P90', {
        median,
        time: duration(reply.p90),
      })
    : median;
};

// "Com uma pessoa agora" e os online, da Agora: um atendente por linha, os que
// estão com mais conversas primeiro, depois os online. A pessoa que tem conversa
// e não está na lista dos atendentes (removida, ou de fora do recorte) entra
// também, sem o ponto de online.
// A hora da leitura no fuso da conta, o do período da Equipe.
const nowTime = computed(() =>
  props.nowAt ? clock(props.nowAt, props.report?.period?.timezone) : ''
);
const nowKey = computed(() => {
  const key = KEY_LABELS.value[props.now?.with_person?.per_person_key];
  return key
    ? t('ANALYTICS_REPORTS.TEAM.NOW.KEY', {
        key,
      })
    : '';
});
const nowPeople = computed(() => {
  if (!props.now) return [];
  const rows = new Map(
    (props.now.online?.people || []).map(person => [
      person.user_id,
      { ...person, count: 0 },
    ])
  );
  (props.now.with_person?.per_person || []).forEach(person => {
    const row = rows.get(person.user_id);
    if (row) row.count = person.count;
    else
      rows.set(person.user_id, {
        user_id: person.user_id,
        name: person.name,
        online: null,
        teams: [],
        count: person.count,
      });
  });
  return [...rows.values()].sort(
    (a, b) =>
      b.count - a.count ||
      Number(Boolean(b.online)) - Number(Boolean(a.online)) ||
      String(a.name || '').localeCompare(String(b.name || ''))
  );
});
</script>

<template>
  <AnalyticsPanel
    data-test-id="analytics-team"
    :title="t('ANALYTICS_REPORTS.TEAM.TITLE')"
    :description="t('ANALYTICS_REPORTS.TEAM.DESCRIPTION')"
  >
    <p v-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('ANALYTICS_REPORTS.TEAM.FAILED') }}
    </p>

    <template v-else-if="report">
      <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div
          v-for="card in cards"
          :key="card.key"
          :data-test-id="`analytics-team-${card.key}`"
          class="flex flex-col gap-1 p-3 min-w-0 rounded-lg border border-n-weak"
        >
          <span class="text-sm text-n-slate-11">{{ card.label }}</span>
          <MetricValue
            :data-test-id="`analytics-team-${card.key}-value`"
            :value="card.value"
            :format="card.format"
            :reason="card.reason"
            class="text-2xl font-medium tabular-nums break-words text-n-slate-12"
          />
          <span
            v-for="(hint, index) in card.hints.filter(Boolean)"
            :key="index"
            class="text-xs text-n-slate-11"
          >
            {{ hint }}
          </span>
        </div>
      </div>

      <div class="grid gap-6 min-w-0 lg:grid-cols-2">
        <div class="flex flex-col gap-3 min-w-0">
          <h3 class="text-xs font-medium text-n-slate-11">
            {{ t('ANALYTICS_REPORTS.TEAM.ENDING') }}
          </h3>
          <ul class="flex flex-col gap-3 list-none">
            <li
              v-for="ending in endings"
              :key="ending.key"
              :data-test-id="`analytics-team-${ending.key}`"
              class="min-w-0"
            >
              <div class="flex flex-wrap gap-x-2 items-baseline text-sm">
                <span class="font-medium text-n-slate-12">
                  {{ ending.label }}
                </span>
                <MetricValue
                  :data-test-id="`analytics-team-${ending.key}-value`"
                  :value="ending.value"
                  :format="number"
                  :reason="noHandoffsReason"
                  class="font-medium tabular-nums text-n-slate-12"
                />
                <span
                  v-if="shareOf(ending.value) !== null"
                  class="text-xs text-n-slate-11"
                >
                  {{ shareHint(ending.value) }}
                </span>
              </div>
              <ProportionBar
                v-if="shareOf(ending.value) !== null"
                class="mt-1 h-1.5"
                :segments="[
                  { key: ending.key, value: ending.value, fill: ending.fill },
                ]"
                :total="handedOff"
              />
              <p
                v-if="ending.hint"
                :data-test-id="`analytics-team-${ending.key}-hint`"
                class="mt-1 mb-0 text-xs text-n-slate-11"
              >
                {{ ending.hint }}
              </p>
            </li>
          </ul>

          <template v-if="paths.length">
            <h3 class="mt-2 text-xs font-medium text-n-slate-11">
              {{ t('ANALYTICS_REPORTS.TEAM.BY_PATH') }}
            </h3>
            <ul
              class="flex flex-col list-none divide-y divide-n-weak"
              data-test-id="analytics-team-paths"
            >
              <li
                v-for="item in paths"
                :key="item.path || 'none'"
                class="flex gap-3 justify-between items-baseline py-2 text-sm first:pt-0"
              >
                <span class="truncate text-n-slate-12">
                  {{ pathLabel(item.path) }}
                </span>
                <span class="tabular-nums text-n-slate-11">
                  {{ number(item.count) }}
                </span>
              </li>
            </ul>
          </template>
        </div>

        <div class="flex flex-col gap-2 min-w-0">
          <h3 class="text-xs font-medium text-n-slate-11">
            {{ t('ANALYTICS_REPORTS.TEAM.PER_PERSON') }}
          </h3>
          <p class="mb-0 text-xs text-n-slate-11">
            {{ t('ANALYTICS_REPORTS.TEAM.PER_PERSON_HINT') }}
          </p>
          <ul
            v-if="personKeys.length"
            data-test-id="analytics-team-keys"
            class="flex flex-col gap-0.5 text-xs list-none text-n-slate-11"
          >
            <li v-for="key in personKeys" :key="key">{{ key }}</li>
          </ul>
          <MetricValue v-if="noHandoffsReason" :reason="noHandoffsReason" />
          <p v-else-if="!people.length" class="mb-0 text-sm text-n-slate-11">
            {{ t('ANALYTICS_REPORTS.TEAM.PER_PERSON_EMPTY') }}
          </p>
          <ul
            v-else
            class="flex flex-col list-none divide-y divide-n-weak"
            data-test-id="analytics-team-people"
          >
            <li
              v-for="person in people"
              :key="person.user_id"
              class="flex flex-col gap-1.5 py-3 min-w-0 first:pt-0"
            >
              <div class="flex gap-3 justify-between items-baseline text-sm">
                <span class="font-medium truncate text-n-slate-12">
                  {{ person.name || t('ANALYTICS_REPORTS.TEAM.REMOVED_USER') }}
                </span>
                <span class="whitespace-nowrap tabular-nums text-n-slate-11">
                  {{
                    t(
                      'ANALYTICS_REPORTS.TEAM.HANDOFFS_COUNT',
                      { count: number(person.handoffs) },
                      person.handoffs
                    )
                  }}
                </span>
              </div>
              <p
                v-if="person.first_reply_seconds"
                data-test-id="analytics-team-person-numbers"
                class="flex flex-wrap gap-x-3 gap-y-0.5 mb-0 text-xs tabular-nums text-n-slate-11"
              >
                <span>{{ personFirstReply(person) }}</span>
                <span>
                  {{
                    t('ANALYTICS_REPORTS.TEAM.PERSON.RESOLVED', {
                      count: number(person.resolved),
                    })
                  }}
                </span>
                <span>
                  {{
                    t('ANALYTICS_REPORTS.TEAM.PERSON.RETURNED', {
                      count: number(person.returned_to_ai),
                    })
                  }}
                </span>
              </p>
              <ul
                v-if="person.top_reasons.length"
                class="flex flex-wrap gap-1.5 list-none"
              >
                <li
                  v-for="reason in person.top_reasons"
                  :key="reason.reason"
                  class="px-1.5 py-0.5 max-w-full text-xs rounded break-words bg-n-amber-3 text-n-amber-11"
                >
                  {{
                    t('ANALYTICS_REPORTS.TEAM.REASON_COUNT', {
                      reason: reason.reason,
                      count: number(reason.count),
                    })
                  }}
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </template>

    <!-- A Agora, lida uma vez com a aba: não se refaz, e diz de quando é. -->
    <section
      v-if="now || nowFailed"
      data-test-id="analytics-team-now"
      class="flex flex-col gap-2 pt-4 min-w-0 border-t border-n-weak"
    >
      <header
        class="flex flex-wrap gap-x-4 gap-y-1 justify-between items-baseline"
      >
        <h3 class="text-xs font-medium text-n-slate-11">
          {{
            nowTime
              ? t('ANALYTICS_REPORTS.TEAM.NOW.TITLE_AT', { time: nowTime })
              : t('ANALYTICS_REPORTS.TEAM.NOW.TITLE')
          }}
        </h3>
        <span
          v-if="now?.online"
          data-test-id="analytics-team-now-online"
          class="text-xs text-n-slate-11"
        >
          {{
            t('ANALYTICS_REPORTS.TEAM.NOW.ONLINE', {
              count: number(now.online.count),
              total: number(now.online.total),
            })
          }}
        </span>
      </header>
      <p v-if="nowFailed" class="mb-0 text-sm text-n-ruby-11">
        {{ t('ANALYTICS_REPORTS.TEAM.FAILED') }}
      </p>
      <template v-else>
        <p
          class="flex flex-wrap gap-x-1.5 items-center mb-0 text-xs text-n-slate-11"
        >
          <span class="rounded-full size-2" :class="QUEM_CORES.pessoa.bg" />
          <span>{{ t('ANALYTICS_REPORTS.TEAM.NOW.WITH_PERSON') }}</span>
          <span v-if="nowKey" data-test-id="analytics-team-now-key">
            {{ nowKey }}
          </span>
        </p>
        <ul
          data-test-id="analytics-team-now-people"
          class="grid grid-cols-1 gap-x-6 gap-y-1 text-sm list-none sm:grid-cols-2 xl:grid-cols-3"
        >
          <li
            v-for="person in nowPeople"
            :key="person.user_id"
            class="flex gap-3 justify-between items-center min-w-0"
          >
            <span class="flex gap-2 items-center min-w-0">
              <span
                v-if="person.online !== null"
                class="flex-shrink-0 rounded-full size-2"
                :class="person.online ? 'bg-n-teal-9' : 'bg-n-slate-7'"
              />
              <span
                class="truncate"
                :class="
                  person.online === false
                    ? 'text-n-slate-10'
                    : 'text-n-slate-12'
                "
              >
                {{ person.name || t('ANALYTICS_REPORTS.TEAM.REMOVED_USER') }}
              </span>
            </span>
            <span class="whitespace-nowrap tabular-nums text-n-slate-11">
              {{
                t(
                  'ANALYTICS_REPORTS.TEAM.NOW.COUNT',
                  { count: number(person.count) },
                  person.count
                )
              }}
            </span>
          </li>
        </ul>
      </template>
    </section>
  </AnalyticsPanel>
</template>
